/**
 * normalizeFlowDoc: turn anything (a stored document, a React Flow export,
 * hand-written JSON, garbage) into a valid document without throwing. Also the
 * cleaners the edits reuse for steps, links and host references. Moved out of
 * model.ts as is.
 */
import {
  FLOW_DIRECTIONS,
  FLOW_DOC_VERSION,
  FLOW_EDGE_KINDS,
  FLOW_EDGE_STYLES,
  FLOW_HANDLES,
  FLOW_LIMITS,
  FLOW_NODE_KINDS,
  FLOW_NODE_SIZE,
  FLOW_REF_TYPES,
  FLOW_TONES,
  type FlowDirection,
  type FlowEdge,
  type FlowLink,
  type FlowMapDoc,
  type FlowNode,
  type FlowNodeKind,
  type FlowPoint,
  type FlowRef,
  type FlowStep,
} from "../types.js";
import {
  isObj,
  oneOf,
  isFlowId,
  makeFlowId,
  cleanText,
  cleanOptionalText,
  isSafeHref,
  coord,
  cleanPoint,
  cleanSize,
  canonicalNode,
  canonicalEdge,
  canonicalDoc,
  orderNodes,
  type Obj,
} from "./shared.js";

// ---------------------------------------------------------------------------
// Normalise
// ---------------------------------------------------------------------------

/** React Flow's built-in node types, read when a React Flow export is imported. */
const RF_TYPE_TO_KIND: Readonly<Record<string, FlowNodeKind>> = { input: "start", output: "end", default: "step" };

function kindOf(raw: Obj): FlowNodeKind {
  const k = raw.kind ?? raw.type;
  if (oneOf(FLOW_NODE_KINDS, k)) return k;
  // Own keys only: a type called "constructor" or "toString" must not read Object.prototype.
  const mapped = typeof k === "string" && Object.prototype.hasOwnProperty.call(RF_TYPE_TO_KIND, k) ? RF_TYPE_TO_KIND[k] : undefined;
  return mapped ?? "step";
}

export function cleanSteps(v: unknown): FlowStep[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const taken = new Set<string>();
  const out: FlowStep[] = [];
  for (const raw of v) {
    if (out.length >= FLOW_LIMITS.stepsPerNode) break;
    const r: Obj | null = typeof raw === "string" ? { text: raw } : isObj(raw) ? raw : null;
    if (!r) continue;
    const text = cleanOptionalText(r.text ?? r.title ?? r.label, FLOW_LIMITS.text);
    if (text === undefined) continue;
    const id = isFlowId(r.id) && !taken.has(r.id) ? r.id : makeFlowId("s", taken);
    taken.add(id);
    const step: FlowStep = { id, text };
    if (r.done === true) step.done = true;
    out.push(step);
  }
  return out.length ? out : undefined;
}

export function cleanLinks(v: unknown, selfId: string, nodeIds: ReadonlySet<string>): FlowLink[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const taken = new Set<string>();
  const out: FlowLink[] = [];
  for (const raw of v) {
    if (out.length >= FLOW_LIMITS.linksPerNode) break;
    if (!isObj(raw)) continue;
    const hrefRaw = typeof raw.href === "string" ? raw.href.trim() : "";
    const href = hrefRaw && isSafeHref(hrefRaw) ? hrefRaw : undefined;
    const nodeId = isFlowId(raw.nodeId) && raw.nodeId !== selfId && nodeIds.has(raw.nodeId) ? raw.nodeId : undefined;
    if (!href && !nodeId) continue;
    const id = isFlowId(raw.id) && !taken.has(raw.id) ? raw.id : makeFlowId("l", taken);
    taken.add(id);
    const link: FlowLink = { id, label: cleanText(raw.label ?? raw.title, FLOW_LIMITS.label) ?? "" };
    if (href) link.href = href;
    if (nodeId) link.nodeId = nodeId;
    out.push(link);
  }
  return out.length ? out : undefined;
}

export function cleanRef(v: unknown): FlowRef | undefined {
  if (!isObj(v) || !oneOf(FLOW_REF_TYPES, v.type) || !isFlowId(v.id)) return undefined;
  return { type: v.type, id: v.id };
}

/** An empty map. */
export function emptyFlowDoc(direction?: FlowDirection): FlowMapDoc {
  return direction ? { version: FLOW_DOC_VERSION, direction, nodes: [], edges: [] } : { version: FLOW_DOC_VERSION, nodes: [], edges: [] };
}

/**
 * True for any object carrying `nodes` and `edges` arrays. Such an object is a
 * graph whose ids are local references, so an id reseeder must copy it as it is.
 */
export function isFlowDocShape(v: unknown): boolean {
  return isObj(v) && Array.isArray(v.nodes) && Array.isArray(v.edges);
}

/** A node as pass 1 reads it, with the raw input pass 2 still needs. */
type Draft = { node: FlowNode; raw: Obj; data: Obj };

/**
 * Turn anything into a valid document. Never throws. Accepts this module's own
 * shape and a React Flow export (`type`, `data.label`, `parentNode`, `style`).
 * Idempotent: normalising a normalised document returns an equal document.
 */
export function normalizeFlowDoc(input: unknown): FlowMapDoc {
  const src: Obj = isObj(input) ? input : {};
  const rawNodes = Array.isArray(src.nodes) ? src.nodes.filter(isObj).slice(0, FLOW_LIMITS.nodes) : [];
  const rawEdges = Array.isArray(src.edges) ? src.edges.filter(isObj) : [];

  // Pass 1: ids, kinds and the node's own fields. Cross references wait for pass 2.
  // Every id stays with its first owner, and all of them are reserved before a new
  // id is minted: otherwise a duplicate renamed to "n3" could take the id a later
  // node really has, and that node's arrows would quietly move to the duplicate.
  const firstOwner = new Map<string, number>();
  rawNodes.forEach((raw, i) => {
    if (isFlowId(raw.id) && !firstOwner.has(raw.id)) firstOwner.set(raw.id, i);
  });
  const taken = new Set<string>(firstOwner.keys());
  const drafts: Draft[] = [];
  let unplaced = 0;
  for (const [i, raw] of rawNodes.entries()) {
    const data: Obj = isObj(raw.data) ? raw.data : {};
    const id = isFlowId(raw.id) && firstOwner.get(raw.id) === i ? raw.id : makeFlowId("n", taken);
    taken.add(id);
    const kind = kindOf(raw);
    let position = cleanPoint(raw.position);
    if (!position) {
      // Deterministic fallback grid; the author can tidy it with auto layout.
      position = { x: (unplaced % 4) * 280, y: Math.floor(unplaced / 4) * 160 };
      unplaced++;
    }
    const node: FlowNode = { id, kind, position, title: cleanText(raw.title ?? data.title ?? data.label, FLOW_LIMITS.title) ?? "" };
    const description = cleanOptionalText(raw.description ?? data.description, FLOW_LIMITS.text);
    if (description !== undefined) node.description = description;
    const tone = raw.tone ?? data.tone;
    if (oneOf(FLOW_TONES, tone)) node.tone = tone;
    const steps = cleanSteps(raw.steps ?? data.steps);
    if (steps) node.steps = steps;
    if (kind === "group") {
      const style: Obj = isObj(raw.style) ? raw.style : {};
      node.size =
        cleanSize(raw.size) ??
        cleanSize({ width: raw.width ?? style.width, height: raw.height ?? style.height }) ?? { ...FLOW_NODE_SIZE.group };
    }
    const ref = cleanRef(raw.ref ?? data.ref);
    if (ref) node.ref = ref;
    drafts.push({ node, raw, data });
  }

  // Pass 2: links to other nodes and group membership.
  const byId = new Map(drafts.map((d) => [d.node.id, d] as const));
  const nodeIds = new Set(byId.keys());
  /** The node a draft names as its parent, when that node exists (React Flow allows any depth). */
  const claimed = (d: Draft): string | undefined => {
    const p = d.raw.parentId ?? d.raw.parentNode;
    return isFlowId(p) && p !== d.node.id && nodeIds.has(p) ? p : undefined;
  };
  /**
   * Where a draft sits on the canvas: its own position plus every parent's up the
   * chain it names. React Flow stores a child relative to its parent at any depth;
   * the model keeps one level (a group and its children), so a parent it refuses
   * hands its offset down, or the node would jump.
   */
  const onCanvas = (d: Draft): FlowPoint => {
    let { x, y } = d.node.position;
    const seen = new Set([d.node.id]);
    for (let p = claimed(d); p !== undefined && !seen.has(p); ) {
      seen.add(p);
      const up = byId.get(p);
      if (!up) break;
      x += up.node.position.x;
      y += up.node.position.y;
      p = claimed(up);
    }
    return { x: coord(x), y: coord(y) };
  };
  const nodes = drafts.map((d) => {
    const { node, raw, data } = d;
    const out: FlowNode = { ...node };
    const links = cleanLinks(raw.links ?? data.links, node.id, nodeIds);
    if (links) out.links = links;
    const parentId = claimed(d);
    if (parentId !== undefined) {
      // A step inside a group stays relative to it; any other parent is refused
      // and the node keeps its place on the canvas.
      if (node.kind !== "group" && byId.get(parentId)?.node.kind === "group") out.parentId = parentId;
      else out.position = onCanvas(d);
    }
    return canonicalNode(out);
  });

  // Edges: both ends must exist, no self loop, one arrow per ordered pair.
  const edges: FlowEdge[] = [];
  const edgeIds = new Set<string>();
  const pairs = new Set<string>();
  for (const raw of rawEdges) {
    if (edges.length >= FLOW_LIMITS.edges) break;
    const { source, target } = raw;
    if (!isFlowId(source) || !isFlowId(target) || source === target) continue;
    if (!nodeIds.has(source) || !nodeIds.has(target)) continue;
    const pair = `${source}>${target}`;
    if (pairs.has(pair)) continue;
    pairs.add(pair);
    const id = isFlowId(raw.id) && !edgeIds.has(raw.id) ? raw.id : makeFlowId("e", edgeIds);
    edgeIds.add(id);
    const data: Obj = isObj(raw.data) ? raw.data : {};
    const e: FlowEdge = { id, source, target };
    if (oneOf(FLOW_HANDLES, raw.sourceHandle)) e.sourceHandle = raw.sourceHandle;
    if (oneOf(FLOW_HANDLES, raw.targetHandle)) e.targetHandle = raw.targetHandle;
    const label = cleanOptionalText(raw.label ?? data.label, FLOW_LIMITS.label);
    if (label !== undefined) e.label = label;
    const kind = raw.kind ?? data.kind;
    if (oneOf(FLOW_EDGE_KINDS, kind) && kind !== "default") e.kind = kind;
    edges.push(canonicalEdge(e));
  }

  const doc: FlowMapDoc = { version: FLOW_DOC_VERSION, nodes: orderNodes(nodes), edges };
  if (oneOf(FLOW_DIRECTIONS, src.direction)) doc.direction = src.direction;
  if (oneOf(FLOW_EDGE_STYLES, src.edgeStyle)) doc.edgeStyle = src.edgeStyle;
  return canonicalDoc(doc);
}
